import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { ProdCategoryComponent } from 'app/modules/well-healthiness/components/prod-category/prod-category.component';
import { WhCategoryComponent } from 'app/modules/well-healthiness/components/wh-category/wh-category.component';
import { WellHealthinessService } from 'app/modules/well-healthiness/services/well-healthiness.service';
import {
    WellHealthinessCategoryResponse,
    ProductionStatusCategoryResponse,
} from 'app/modules/well-healthiness/model';
import module from 'app/modules/well-healthiness/well-healthiness.module';
import { EMPTY, merge, Subject, switchMap, takeUntil } from 'rxjs';

interface WhFieldSummary {
    fieldName: string;
    healthyPercentage: number;
    healthy: number;
    healthyMoc: number;
    unhealthy: number;
    unhealthyMoc: number;
    unhealthyHighRisk: number;
    list: any[];
}

@Component({
    selector: 'app-well-healthiness-field-summary',
    templateUrl: './field-summary.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [...module, WhCategoryComponent, ProdCategoryComponent],
    styleUrls: ['./field-summary.page.scss'],
})
export class FieldSummaryPage implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator) private readonly _paginator: MatPaginator;
    @ViewChild(MatSort) private readonly _sort: MatSort;
    private readonly _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any;
    startingIndex: number = 0;
    isLoading: boolean = false;
    isWhCategory: boolean = true;
    whCategoryData: WellHealthinessCategoryResponse;
    prodCategoryData: ProductionStatusCategoryResponse;

    constructor(
        public service: WellHealthinessService,
        private readonly _formBuilder: UntypedFormBuilder,
        private readonly router: Router,
        private readonly _changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.setSearch();
        this.setDataTable();
        this.fetchFieldSummaryData();
        this.fetchWHCategoryByWellHealthiness();
        this.fetchWHCategoryByProductionStatus();
    }

    fetchWHCategoryByWellHealthiness() {
        this.service
            .getWellHealthinessCategoryByWellHealthiness()
            .subscribe((response) => {
                this.whCategoryData = response;
                this._changeDetectorRef.markForCheck();
            });
    }

    fetchWHCategoryByProductionStatus() {
        this.service
            .getWellHealthinessCategoryByProductionStatus()
            .subscribe((response) => {
                this.prodCategoryData = response;
                this._changeDetectorRef.markForCheck();
            });
    }

    fetchFieldSummaryData() {
        const params = this.searchForm.getRawValue();
        this.service
            .getWellHealthinessFieldsList(params)
            .subscribe((response) => {
                if (response?.data?.data) {
                    const transformedData = response.data.data.map((item) => ({
                        fieldName: item.fieldName,
                        healthyPercentage: item.healthyPercentage,
                        healthy: item.healthyCount,
                        healthyMoc: item.healthyWithMocCount,
                        unhealthy: item.unhealthyCount,
                        unhealthyMoc: item.unhealthyWithMocCount,
                        unhealthyHighRisk: item.unhealthyWithHighRiskCount,
                        list: item.platformList || [],
                    }));

                    this.dt.page.totalPages = response.data.totalPages;
                    this.dt.page.totalRecords = response.data.totalRecords;
                    this.dt.page.size = params.PageSize;
                    this.dt.rows = transformedData.map((row) => ({
                        ...row,
                        expanded: false,
                    }));

                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;
                    this.searchForm.patchValue({ Page: 1 });
                    this.fetchFieldSummaryData();
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page)
                .pipe(
                    switchMap(() => {
                        const pageIndex = this._paginator.pageIndex + 1;
                        this.searchForm.patchValue({ Page: pageIndex });
                        this.fetchFieldSummaryData();
                        return EMPTY;
                    }),
                    takeUntil(this._unsubscribeAll),
                )
                .subscribe();
        }
    }

    setSearch() {
        this.searchForm = this._formBuilder.group({
            Page: [0],
            PageSize: [10],
            OrderBy: [null],
            OrderDir: [null],
        });

        this.searchForm
            .get('PageSize')
            .valueChanges.pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
                this.dt.page.offset = 0;
                this.searchForm.patchValue(
                    { Page: this.dt.page.offset + 1 },
                    { emitEvent: false },
                );
                this.fetchFieldSummaryData();
            });
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0;
        this.searchForm.patchValue(
            { Page: this.dt.page.offset + 1 },
            { emitEvent: false },
        );
        this.fetchFieldSummaryData();
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };

        return {
            column: {
                mode: ColumnMode.flex,
                header: [
                    {
                        key: 'fieldLongName',
                        name: 'Field',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                        },
                    },
                    {
                        key: 'healthyPercentage',
                        name: 'Healthy (%)',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                        },
                    },
                    {
                        key: 'healthy',
                        name: 'Healthy',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                        },
                    },

                    {
                        key: 'healthyWithMoc',
                        name: 'Healthy With MOC',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                        },
                    },
                    {
                        key: 'unhealthy',
                        name: 'Unhealthy',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                        },
                    },
                    {
                        key: 'unhealthyWithMoc',
                        name: 'Unhealthy With MOC',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                        },
                    },
                    {
                        key: 'unhealthyWithHighRisk',
                        name: 'Unhealthy With High Risk',
                        props: {
                            flexGrow: 1,
                            sortable: true,
                        },
                    },
                ],
            },
        };
    }

    toggleExpandRow(row: any) {
        row.expanded = !row.expanded;
    }

    toStringSummary() {
        this.router.navigate([`/wim/well-healthiness/dashboard/string`]);
    }

    reset() {
        this.setSearch();
        this.fetchFieldSummaryData();
    }

    trackByFn(index: number): number {
        return index;
    }

    downloadFieldsSummaryExcel() {
        this.service
            .getWellHealthinessFieldsSummaryExport()
            .subscribe((blob) => {
                const { date, time } = this.getFormattedDateTime();
                const url = globalThis.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `Well_Healthiness_Fields_Summary_${date}_${time}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                globalThis.URL.revokeObjectURL(url);
            });
    }

    private getFormattedDateTime() {
        const now = new Date();
        const date = now.toISOString().split('T')[0]; // YYYY-MM-DD
        const time = now.toTimeString().split(' ')[0]; // HH:MM:SS
        return { date, time };
    }
}
